import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { findApp, type AppId } from '@/apps/registry';
import { useAuthStore } from '@/store/authStore';

/**
 * Carrega uma app filha via iframe e injeta o JWT via postMessage.
 *
 * Padrão de comunicação:
 *   1. Iframe carrega → posta { type: 'gg-monitor-ready' }
 *   2. Shell responde com { type: 'jwt', token: <jwt> }
 *   3. App filha guarda em localStorage para sobreviver a reload
 */
export function AppFrame() {
  const { appId } = useParams<{ appId: AppId }>();
  const jwt = useAuthStore((s) => s.jwt);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeReady, setIframeReady] = useState(false);

  const app = appId ? findApp(appId) : undefined;

  // Listener para handshake inicial da app filha
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      // Validar origem (em produção)
      if (import.meta.env.PROD) {
        const url = new URL(app?.url ?? '');
        if (event.origin !== url.origin) return;
      }

      const data = event.data as { type?: string };
      if (data?.type === 'gg-monitor-ready') {
        setIframeReady(true);
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [app?.url]);

  // Enviar JWT quando iframe sinaliza ready
  useEffect(() => {
    if (!iframeReady || !jwt || !iframeRef.current?.contentWindow || !app) return;
    const url = new URL(app.url);
    iframeRef.current.contentWindow.postMessage(
      { type: 'jwt', token: jwt },
      url.origin,
    );
  }, [iframeReady, jwt, app]);

  if (!app) {
    return (
      <div className="flex-1 flex items-center justify-center text-surf-text-secondary">
        App não encontrada: <code className="ml-2 font-mono">{appId}</code>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col">
      <header className="flex items-center justify-between px-6 py-3 border-b border-surf-border bg-surf-bg-1">
        <div>
          <h1 className="text-sm font-semibold">{app.label}</h1>
          <p className="text-2xs text-surf-text-tertiary">{app.description}</p>
        </div>
        <a
          href={app.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-2xs text-surf-text-tertiary hover:text-surf-accent font-mono transition-colors"
        >
          ↗ abrir em nova aba
        </a>
      </header>

      <iframe
        ref={iframeRef}
        src={app.url}
        title={app.label}
        className="flex-1 w-full border-0 bg-surf-bg-0"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
      />
    </div>
  );
}
